import {GitMergeIcon} from '@primer/octicons-react'
import {announce} from '@github-ui/aria-live'
import {useRestoreHeadRefMutation} from '@github-ui/mergebox/use-restore-head-ref-mutation'
import {Button} from '@primer/react'
import {useRef} from 'react'

import {usePullRequestState} from '../../../../hooks/use-pull-request-state'
import {PullRequestState} from '../../../../types'
import {useInvalidatePrState} from '../../../../hooks/use-invalidate-pr-state'
import type {ViewerRole} from '../deciders/use-decide-next-action'
import {useMergeabilityData} from '../mergeability/use-mergeability-data'
import {FailureDialog} from '../../../FailureDialog'
import {ForYouItem} from '../shared/ForYouItem'
import type {ForYouItemAction} from '../shared/for-you-item.types'
import {DeleteHeadBranch, shouldShowDeleteHeadBranch} from './DeleteHeadBranch'
import {MergedDescription} from './MergedDescription'
import {RevertPRAction} from './RevertPRAction'
import {findPreviousTabbableElementOutsideRow} from './focus-utils'

export function shouldShowMergedPR(pullRequestState: PullRequestState) {
  return pullRequestState === PullRequestState.Merged
}

export function MergedPR({userRole}: {userRole?: ViewerRole}) {
  const pullRequestState = usePullRequestState()
  const mergeabilityData = useMergeabilityData()

  if (!shouldShowMergedPR(pullRequestState)) return null

  const isAuthor = !userRole || userRole === 'author'

  if (!isAuthor) {
    return <MergedStatus actions={[{id: 'revert', content: <RevertPRAction />}]} />
  }

  if (shouldShowDeleteHeadBranch(mergeabilityData)) {
    return <DeleteHeadBranch />
  }

  const canRestoreHeadRef = Boolean(mergeabilityData?.mergeBoxPullRequest.viewerCanRestoreHeadRef)

  return (
    <MergedStatus
      actions={[
        {
          id: 'restore',
          content: canRestoreHeadRef ? <RestoreBranchAction /> : null,
        },
        {id: 'revert', content: <RevertPRAction />},
      ]}
    />
  )
}

function MergedStatus({actions}: {actions?: readonly ForYouItemAction[]}) {
  return (
    <ForYouItem
      itemId="merged"
      visual={{kind: 'icon', icon: GitMergeIcon, tone: 'done', glyphSize: 12}}
      title="Merged"
      subText={<MergedDescription />}
      actions={actions}
    />
  )
}

function RestoreBranchAction() {
  const invalidatePrState = useInvalidatePrState()
  const restoreButtonRef = useRef<HTMLButtonElement>(null)
  const restoreHeadRefMutation = useRestoreHeadRefMutation({
    onSuccess: () => {
      findPreviousTabbableElementOutsideRow(restoreButtonRef.current)?.focus()
      announce('Branch restored')
      invalidatePrState()
    },
  })

  return (
    <>
      <Button
        ref={restoreButtonRef}
        size="small"
        loading={restoreHeadRefMutation.isPending}
        loadingAnnouncement="Restoring branch"
        onClick={() => restoreHeadRefMutation.mutate()}
        labelWrap
      >
        Restore branch
      </Button>
      {restoreHeadRefMutation.isError && (
        <FailureDialog
          title="Failed to restore branch"
          message="An error occurred while restoring the branch. Please try again."
          details={restoreHeadRefMutation.error.message}
          onRetry={() => restoreHeadRefMutation.mutate()}
          onDismiss={() => restoreHeadRefMutation.reset()}
        />
      )}
    </>
  )
}
